import * as Yup from "yup";
import { toast } from "react-toastify";
import { Form, Formik } from "formik";
import { Box, Grid, MenuItem, Typography } from "@mui/material";
import { useAppDispatch } from "../../../../app/features/store/store";
import getAxiosError from "../../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import { STATES_LIST } from "../../../fdp/FdpConstants";
import { AddStorePkgRequest } from "../../../../app/models/requestModels/storePkgRequest";
import FormikDatePicker from "../../../../components/common/formikFields/FormikDatePicker";
import { addStorePackage } from "../../../../app/features/dedicatedReceiving/storeManifestSlice";
import { useCallback, useEffect, useRef, useState } from "react";
import agent from "../../../../app/api/agent";
import { FdpResponse } from "../../../../app/models/responseModels/FdpResponse";
import { StorePackageValidateRequest } from "../../../../app/models/requestModels/StoreManifestValidationRequest";
import { FieldValidationDetails } from "../../../../app/models/responseModels/FieldValidationDetails";

interface AddNewPackageModalProps {
  storePrimaryId: number;
  closeAddPackageModal: CallableFunction;
  onCompleted: CallableFunction;
}
function AddStorePkgFormModel(
  toteId: string,
  orderId: string,
  storePrimaryId: number,
  dropNumber: string,
  poNumber: string,
  driverUserId: string,
  displayTripId: string,
  customerAddressLine1: string,
  customerCity: string,
  customerState: string,
  customerPostalcode: string,
  scheduledDeliveryDate: Date | undefined
): AddStorePkgRequest {
  return {
    toteId: toteId.trim(),
    orderId: orderId.trim(),
    storePrimaryId: storePrimaryId,
    dropNumber: dropNumber.trim(),
    poNumber: poNumber.trim(),
    driverUserId: driverUserId.trim(),
    displayTripId: displayTripId.trim(),
    customerAddressLine1: customerAddressLine1.trim(),
    customerCity: customerCity.trim(),
    customerState: customerState.trim(),
    customerPostalcode: customerPostalcode.trim(),
    scheduledDeliveryDate: scheduledDeliveryDate,
  };
}

const AddNewPackageModal = ({
  closeAddPackageModal,
  onCompleted,
  storePrimaryId,
}: AddNewPackageModalProps) => {
  const dispatch = useAppDispatch();
  const formikRef = useRef<any>(null);
  const initialValues: any = {
    fdp: "",
    toteId: "",
    orderId: "",
    dropNumber: "",
    poNumber: "",
    storePrimaryId: storePrimaryId,
    driverUserId: "",
    displayTripId: "",
    customerAddressLine1: "",
    customerCity: "",
    customerState: "",
    customerPostalcode: "",
    scheduledDeliveryDate: undefined,
  };
  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [associatedFdpsToStore, setAssociatedFdpsToStore] = useState<
    FdpResponse[]
  >([]);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [driverUserIds, setDriverUserIds] = useState<string[]>([]);
  const [validationDetails, setValidationDetails] = useState<
    FieldValidationDetails[]
  >([]);

  const validationSchema = Yup.object().shape({
    fdp: Yup.string().trim().required("FDP is required"),
    toteId: Yup.string().trim().required("Tote ID is required"),
    orderId: Yup.string().trim().required("Order ID is required"),
    dropNumber: Yup.string()
      .trim()
      .matches(/^\d+$/, "Drop Number must be a positive number")
      .required("Drop Number is required"),
    poNumber: Yup.string().trim().required("PO Number is required"),
    driverUserId: Yup.string().trim().required("Driver User ID is required"),
    displayTripId: Yup.string().trim().required("Display Trip ID is required"),
    customerAddressLine1: Yup.string()
      .trim()
      .required("Customer Address Line 1 is required"),
    customerCity: Yup.string().trim().required("Customer City is required"),
    customerState: Yup.string().trim().required("Customer State is required"),
    customerPostalcode: Yup.string()
      .trim()
      .matches(/^\d{5}$/, "Invalid Customer Postalcode.")
      .required("Customer Postalcode is required"),
    scheduledDeliveryDate: Yup.date()
      .required("Delivery Date is required")
      .typeError("Invalid Date"),
  });

  const customValidate = (values: any) => {
    const errors: { [key: string]: string } = {};
    // Validate using Yup schema first
    try {
      validationSchema.validateSync(values, { abortEarly: false });
    } catch (err: any) {
      if (err.inner) {
        err.inner.forEach((validationError: any) => {
          errors[validationError.path] = validationError.message;
        });
      }
    }
    // Custom validation logic using validationDetails
    validationDetails.forEach((detail) => {
      if (detail.isError && values[detail.field] !== undefined) {
        errors[detail.field] = detail.message;
      }
    });
    return errors;
  };

  // ***** Use Callbacks *****
  const getLinkedFdpsForStore = useCallback(async () => {
    try {
      setFormLoading(true);
      const response = await agent.FdpAgent.getFdpListByStore([storePrimaryId]);
      if (response.isSuccess) {
        setAssociatedFdpsToStore(response.data[0].fdps);
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "FdpsForStoreException",
      });
    } finally {
      setFormLoading(false);
    }
  }, [dispatch, storePrimaryId]);

  const getDriverUserIds = useCallback(async () => {
    try {
      setFormLoading(true);
      if (formikRef.current) {
        const { values } = formikRef.current;
        if (values.fdp) {
          console.log(values);
          formikRef.current.setFieldValue("driverUserId", "");
          const response = await agent.FdpAgent.getFdpDriverEmailsByFdpAndStore(
            values.fdp,
            storePrimaryId
          );
          if (response.isSuccess) {
            setDriverUserIds(response.data);
          }
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "DriveruserIdListException",
      });
    } finally {
      setFormLoading(false);
    }
  }, [dispatch, storePrimaryId]);

  const handleDropNumberValidation = useCallback(async () => {
    setFormLoading(true);
    try {
      if (formikRef.current) {
        const { values } = formikRef.current;
        const driverUserId = values.driverUserId;
        const scheduledDeliveryDate = values.scheduledDeliveryDate;
        const dropNumber = values.dropNumber;
        if (driverUserId && scheduledDeliveryDate && dropNumber) {
          const validationReqBody: StorePackageValidateRequest = {
            storePrimaryId: storePrimaryId,
            fdpId: values.fdpId,
            fdpDriverUserId: driverUserId,
            toteId: values.toteId,
            dropNumber: dropNumber,
            scheduledDeliveryDate: scheduledDeliveryDate,
          };
          const response =
            await agent.StoreManifestAgent.validateNewStorePacakge(
              validationReqBody
            );
          if (response.isSuccess) {
            setValidationDetails(response.data.validationDetails);
          }
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "DropNumberValidationException",
      });
    } finally {
      setFormLoading(false);
    }
  }, [dispatch, storePrimaryId]);

  // ***** Use Effects *****
  useEffect(() => {
    getLinkedFdpsForStore();
  }, [dispatch, getLinkedFdpsForStore]);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.validateForm();
    }
  }, [validationDetails]);

  // ***** Handlers *****
  const handleFdpChange = () => {
    if (timeoutId) {
      clearTimeout(timeoutId); // Clear existing timeout
    }
    const id = setTimeout(() => {
      getDriverUserIds();
    }, 500);
    setTimeoutId(id); // Store timeout ID
  };
  const handleFormValueChange = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const id = setTimeout(() => {
      handleDropNumberValidation();
    }, 1000);
    setTimeoutId(id);
  };

  const handleSubmit = async (values: any) => {
    try {
      const addStorePkgDetails = AddStorePkgFormModel(
        values.toteId,
        values.orderId,
        storePrimaryId,
        values.dropNumber,
        values.poNumber,
        values.driverUserId,
        values.displayTripId,
        values.customerAddressLine1,
        values.customerCity,
        values.customerState,
        values.customerPostalcode,
        values.scheduledDeliveryDate
      );
      const response = await dispatch(
        addStorePackage(addStorePkgDetails)
      ).unwrap();
      if (response.isSuccess) {
        toast.success("Package saved successfully.", {
          toastId: "addNewStorePackageMessage",
        });
        onCompleted();
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error("Package couldn't be saved.", {
        toastId: "addNewStorePackageException",
      });
    }
    return true;
  };
  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validate={customValidate}
      onSubmit={(values: any, { setSubmitting }) => {
        console.log(values);
        setSubmitting(true);
        handleSubmit(values);
        setSubmitting(false);
      }}
    >
      <SubmitModalWithFormik
        title="New Package"
        closeSubmitModal={closeAddPackageModal}
        allowSubmit={false}
        size="lg"
        isLoading={false}
        submitButtonName="Save"
        closeModalAfterSubmit={true}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Form>
            <Grid container columnSpacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  select
                  margin="normal"
                  id="fdp"
                  label="FDP"
                  name="fdp"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  isDisabled={isFormLoading}
                  onChange={(e: any) => {
                    formikRef.current.setFieldValue("fdp", e.target.value);
                    handleFdpChange();
                  }}
                  required
                >
                  {associatedFdpsToStore.length === 0 && (
                    <MenuItem disabled value={0}>
                      No FDPs Associated with the store.
                    </MenuItem>
                  )}
                  {associatedFdpsToStore.map((state, index) => (
                    <MenuItem key={index} value={state.id}>
                      {state.companyName}
                    </MenuItem>
                  ))}
                </FormikTextField>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  select
                  margin="normal"
                  id="driverUserId"
                  label="Driver User ID"
                  name="driverUserId"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                  onChange={(e: any) => {
                    formikRef.current.setFieldValue(
                      "driverUserId",
                      e.target.value
                    );
                    handleFormValueChange();
                  }}
                >
                  {driverUserIds.length === 0 && (
                    <MenuItem disabled>
                      No Driver IDs Available for the FDP.
                    </MenuItem>
                  )}
                  {driverUserIds.map((state, index) => (
                    <MenuItem key={index} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </FormikTextField>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="toteId"
                  label="Tote ID"
                  name="toteId"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                  onChange={(e: any) => {
                    formikRef.current.setFieldValue("toteId", e.target.value);
                    handleFormValueChange();
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="orderId"
                  label="Order ID"
                  name="orderId"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikDatePicker
                  name="scheduledDeliveryDate"
                  label="Scheduled Delivery Date"
                  size="small"
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  required
                  handleOnChange={() => handleFormValueChange()}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="dropNumber"
                  label="Drop Number"
                  name="dropNumber"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                  onChange={(e: any) => {
                    formikRef.current.setFieldValue(
                      "dropNumber",
                      e.target.value
                    );
                    handleFormValueChange();
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="poNumber"
                  label="PO Number"
                  name="poNumber"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="displayTripId"
                  label="Display Trip ID"
                  name="displayTripId"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="customerAddressLine1"
                  label="Customer Address Line 1"
                  name="customerAddressLine1"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="customerCity"
                  label="Customer City"
                  name="customerCity"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  select
                  margin="normal"
                  id="customerState"
                  label="Customer State"
                  name="customerState"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  SelectProps={{
                    MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
                  }}
                  required
                >
                  {STATES_LIST.map((state, index) => (
                    <MenuItem key={index} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </FormikTextField>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="customerPostalcode"
                  label="Customer Postalcode"
                  name="customerPostalcode"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
            </Grid>
            {/* {validationDetails.filter((details) => details.isWarning).length >
              0 && <Typography color="warning.main">Warnings</Typography>} */}
            <Box mt={1}>
              {validationDetails
                .filter((details) => details.isWarning)
                .map((details, index) => (
                  <Typography key={index} color="warning.main">
                    {details.message}
                  </Typography>
                ))}
            </Box>
          </Form>
        </Box>
      </SubmitModalWithFormik>
    </Formik>
  );
};

export default AddNewPackageModal;
